import { loadScript } from '@/utils/loadScript'

import useExchangeAppleToken, { AppleTokenExchangeResponse } from '../api/useExchangeAppleToken'

const APPLE_CLIENT_ID = process.env.NEXT_PUBLIC_APPLE_CLIENT_ID || ''
const APPLE_REDIRECT_URI = process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI || ''
const APPLE_SCRIPT_ID = 'apple-oauth-sdk'
const APPLE_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'

const ensureAppleSdk = (): Promise<void> =>
  loadScript({
    id: APPLE_SCRIPT_ID,
    src: APPLE_SCRIPT_SRC,
  })

/**
 * Hook for handling Apple OAuth.
 * Initiates the Apple Sign In flow and exchanges the authorization code for an auth token.
 *
 * @returns {Object} An object containing:
 *  - initiateAppleAuth: Function to trigger Apple authentication.
 *  - appleTokenExchangeError: Error (if any) from the token exchange process.
 */
export const useAppleAuth = (
  onSuccessCallback?: (response: AppleTokenExchangeResponse) => void,
): {
  initiateAppleAuth: () => void
  appleTokenExchangeError: Error | null
} => {
  const { mutate: exchangeAppleToken, error: appleTokenExchangeError } = useExchangeAppleToken()

  /**
   * Handles the Apple Sign In success event.
   * Extracts the authorization code and user email (if present) and sends them to the backend.
   *
   * @param {AppleSignInSuccessEvent} event - The successful Apple sign-in event.
   */
  const handleAppleResponse = ({ detail }: AppleIDSignInSuccess): void => {
    const code = detail?.authorization?.code || ''
    const user = detail?.user
    const email = user?.email

    // Determine the backend endpoint based on whether user info is provided.
    const urlPath = user ? '/auth/apple_id/registered' : '/auth/apple_id/check'
    exchangeAppleToken(
      { code, email, frontend_app: true, urlPath },
      {
        onSuccess: (response) => {
          if (user) {
            if (!response.registered && code) response.code = code

            response.email = email || ''
            response.firstName = user.name?.firstName || ''
            response.lastName = user.name?.lastName || ''
          } else if (!response.existing && code) {
            response.code = code
          }

          onSuccessCallback?.(response)
        },
      },
    )
  }

  /**
   * Initiates the Apple OAuth popup flow
   */
  const initiateAppleAuth = (): void => {
    const scope = `name email`

    void ensureAppleSdk().then(() => {
      // TODO: Handle the case where AppleID.auth is not available
      if (window && window.AppleID && typeof window.AppleID.auth.init === 'function') {
        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope,
          redirectURI: APPLE_REDIRECT_URI,
          usePopup: true,
        })
        window.AppleID.auth.signIn()
        document.removeEventListener('AppleIDSignInOnSuccess', handleAppleResponse)
        document.addEventListener('AppleIDSignInOnSuccess', handleAppleResponse)
      }
    })
  }

  return {
    initiateAppleAuth,
    appleTokenExchangeError,
  }
}
